import {
  fetchRemoteFlight,
  fetchReplayStatus,
  isReplayGenerationInProgress,
  pollReplayReady,
} from '@/services/sync';
import { isAuthenticated } from '@/services/api';
import { setGenerating } from '@/composables/useReplayGenerating';
import { emitReplayReady } from '@/composables/useReplayReadyEvents';
import {
  showReplayReadyAlert,
  showReplayStillGeneratingAlert,
} from '@/utils/replayReadyAlert';
import { captureFailure, log, warn } from '@/lib/appTelemetry';

export type ReplayPollOptions = {
  flightName?: string;
  /** Called when replay URL is available. If omitted, emits a global ready event and shows an alert. */
  onReady?: (replayUrl: string) => void | Promise<void>;
  /** Called when polling stops without a replay URL. */
  onExhausted?: () => void | Promise<void>;
};

const activePolls = new Map<string, () => void>();

export function isReplayPollActive(remoteId: string): boolean {
  return activePolls.has(remoteId);
}

export function stopReplayPoll(remoteId: string): void {
  const stop = activePolls.get(remoteId);
  if (stop) {
    stop();
    activePolls.delete(remoteId);
  }
}

export function stopAllReplayPolls(): void {
  for (const remoteId of [...activePolls.keys()]) {
    stopReplayPoll(remoteId);
  }
}

/** Poll until replay is ready. Replaces any existing poll for the same flight. */
export function startReplayPoll(remoteId: string, options: ReplayPollOptions = {}): () => void {
  stopReplayPoll(remoteId);
  log('replay', 'poll start', { remoteId });

  const stop = pollReplayReady(
    remoteId,
    async (replayUrl) => {
      stopReplayPoll(remoteId);
      setGenerating(remoteId, false);
      log('replay', 'poll ready', { remoteId });
      if (options.onReady) {
        await options.onReady(replayUrl);
      } else {
        emitReplayReady({ remoteId, replayUrl, flightName: options.flightName });
        await showReplayReadyAlert(options.flightName ?? 'Flight');
      }
    },
    {
      onExhausted: async () => {
        stopReplayPoll(remoteId);
        setGenerating(remoteId, false);
        warn('replay', 'poll exhausted', { remoteId });
        if (options.onExhausted) {
          await options.onExhausted();
        } else {
          await showReplayStillGeneratingAlert();
        }
      },
    },
  );

  activePolls.set(remoteId, stop);
  return () => stopReplayPoll(remoteId);
}

/**
 * Reconcile in-memory "generating" state with the server after resume or notification.
 * Restarts polling when generation is still in progress.
 */
export async function syncGeneratingReplays(remoteIds: string[]): Promise<void> {
  if (!isAuthenticated() || remoteIds.length === 0) return;

  await Promise.all(
    remoteIds.map(async (remoteId) => {
      const flightRes = await fetchRemoteFlight(remoteId);
      const flightName = flightRes.success ? flightRes.data?.name : undefined;

      if (flightRes.success && flightRes.data?.replayUrl) {
        stopReplayPoll(remoteId);
        setGenerating(remoteId, false);
        emitReplayReady({
          remoteId,
          replayUrl: flightRes.data.replayUrl,
          flightName,
        });
        return;
      }

      const statusRes = await fetchReplayStatus(remoteId);
      const status = statusRes.success ? statusRes.data?.status : undefined;
      const statusReplayUrl = statusRes.success ? statusRes.data?.replayUrl : null;

      if (statusReplayUrl) {
        stopReplayPoll(remoteId);
        setGenerating(remoteId, false);
        emitReplayReady({ remoteId, replayUrl: statusReplayUrl, flightName });
        return;
      }

      if (status === 'failed' || status === 'none') {
        stopReplayPoll(remoteId);
        setGenerating(remoteId, false);
        if (status === 'failed') {
          captureFailure('replay', 'generation failed', undefined, { remoteId });
        }
        return;
      }

      if (isReplayGenerationInProgress(status) && !isReplayPollActive(remoteId)) {
        startReplayPoll(remoteId, { flightName });
      }
    }),
  );
}
